import type { ExerciseTemplate } from '@yuan-math/exercises';
import { fraction, gcd, toText } from '@yuan-math/math';
import {
  choiceExercise,
  decimalTolerance,
  expr,
  expressionExercise,
  fractionExercise,
  fractionTrap,
  leadingTerm,
  numericExercise,
  options,
  pickDistinct,
  signedConstant,
  template,
  trap,
} from './authoring.ts';

/**
 * Transformations, inverses, logarithms and trigonometry.
 *
 * Several of these are multiple choice not for convenience but because the
 * error is about *direction* rather than value — whether a shift goes left or
 * right, whether an inverse reflects or reciprocates, whether a coefficient
 * lengthens a wave or shortens it. A student with the direction backwards often
 * produces a number that is right for a different question, and an answer box
 * cannot tell those two apart.
 */

/**
 * Exact coordinates on the unit circle, in LaTeX, for the angles students learn.
 *
 * The 45° family is deliberately absent. There cosine and sine are equal in
 * size, so "you read the wrong axis" and "you lost the quadrant's sign" produce
 * the *same* option — and an item where one answer carries two diagnoses tells
 * the student something that may not be true.
 */
const CIRCLE_VALUES: Record<
  number,
  { cos: string; sin: string; cosText: string; sinText: string; reference: number }
> = {
  30: { cos: '\\frac{\\sqrt{3}}{2}', sin: '\\frac{1}{2}', cosText: '√3/2', sinText: '1/2', reference: 30 },
  60: { cos: '\\frac{1}{2}', sin: '\\frac{\\sqrt{3}}{2}', cosText: '1/2', sinText: '√3/2', reference: 60 },
  120: { cos: '-\\frac{1}{2}', sin: '\\frac{\\sqrt{3}}{2}', cosText: '−1/2', sinText: '√3/2', reference: 60 },
  150: { cos: '-\\frac{\\sqrt{3}}{2}', sin: '\\frac{1}{2}', cosText: '−√3/2', sinText: '1/2', reference: 30 },
  210: { cos: '-\\frac{\\sqrt{3}}{2}', sin: '-\\frac{1}{2}', cosText: '−√3/2', sinText: '−1/2', reference: 30 },
  240: { cos: '-\\frac{1}{2}', sin: '-\\frac{\\sqrt{3}}{2}', cosText: '−1/2', sinText: '−√3/2', reference: 60 },
  300: { cos: '\\frac{1}{2}', sin: '-\\frac{\\sqrt{3}}{2}', cosText: '1/2', sinText: '−√3/2', reference: 60 },
  330: { cos: '\\frac{\\sqrt{3}}{2}', sin: '-\\frac{1}{2}', cosText: '√3/2', sinText: '−1/2', reference: 30 },
};

/** The same value with its sign flipped, for a distractor that ignores the quadrant. */
const flipSign = (latex: string): string => (latex.startsWith('-') ? latex.slice(1) : `-${latex}`);

/**
 * A number for prose rather than for LaTeX.
 *
 * `-4` inside a formula is right, and `-4` in a sentence is a hyphen standing
 * where a minus sign belongs. Prompts and explanations render as text, so they
 * need the real character.
 */
const minus = (value: number | string): string => String(value).replace(/-/g, '\u2212');

/** Rounds to one and to two decimal places, the precisions these answers ask for. */
const round1 = (value: number): number => Math.round(value * 10) / 10;
const round2 = (value: number): number => Math.round(value * 100) / 100;

/**
 * A two-part piecewise definition, drawn as a brace rather than as prose.
 *
 * The conditions are the mathematics here, not decoration: `<` and `≥` are what
 * decide which rule owns the joining value, and a definition written out as a
 * sentence hides exactly that.
 */
const piecewiseLatex = (
  lowerSlope: number,
  lowerShift: number,
  upperSlope: number,
  upperShift: number,
  join: number,
): string =>
  `f(x) = \\begin{cases} ${expr(leadingTerm(lowerSlope), signedConstant(lowerShift))} & x < ${join} \\\\ ${expr(leadingTerm(upperSlope), signedConstant(upperShift))} & x \\ge ${join} \\end{cases}`;

export const FUNCTIONS_HS_TEMPLATES: ExerciseTemplate[] = [
  /* -------------------------------------------------- transformations */
  template({
    id: 'transformation-describe',
    conceptId: 'function-transformations',
    description: 'Say what a change to f(x) does to its graph.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const h = rng.int(1, 9);
      const inside = rng.bool();
      const correct = inside ? `${h} to the right` : `${h} up`;
      const reversed = inside ? `${h} to the left` : `${h} down`;
      const swapped = inside ? `${h} up` : `${h} to the right`;

      return choiceExercise({
        prompt: 'How does this graph compare with the graph of y = f(x)?',
        latex: inside ? `y = f(x - ${h})` : `y = f(x) + ${h}`,
        ...options(
          [
            { label: correct, correct: true },
            { label: reversed, misconceptionId: 'shift-direction-inverted' },
            { label: swapped, misconceptionId: 'inside-outside-swapped' },
            { label: `stretched by a factor of ${h}` },
          ],
          rng.shuffle,
        ),
        summary: inside
          ? `The change is inside the bracket, so it moves the graph horizontally — and backwards. To get the old output you now need an x that is ${h} bigger, so everything shifts ${h} right.`
          : `The change is outside the function, so it acts on the output and lifts the whole graph ${h} up.`,
        hint: 'Outside acts on the output and moves it vertically. Inside acts on the input, and runs backwards.',
      });
    },
  }),

  template({
    id: 'transformed-point',
    conceptId: 'function-transformations',
    description: 'Track a point through a horizontal shift.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const px = rng.int(-8, 8);
      const py = rng.int(-8, 8);
      const h = rng.int(1, difficulty === 4 ? 9 : 5);
      const k = rng.int(1, 9);

      return numericExercise({
        prompt: `The point (${px}, ${py}) lies on y = f(x). What is the x-coordinate of the matching point on the new graph?`,
        latex: `y = f(x - ${h}) + ${k}`,
        value: px + h,
        summary: `Subtracting ${h} inside moves the graph ${h} to the right, so the x-coordinate becomes ${px} + ${h} = ${px + h}. (The +${k} changes the y-coordinate, not this one.)`,
        hint: 'Which x now gives what f used to give at the old one?',
        steps: [
          { label: 'The inside change is horizontal, and runs backwards.', latex: `x \\to x + ${h}` },
          { label: 'Apply it to the point.', latex: `${px} + ${h} = ${px + h}` },
        ],
        traps: [
          // Reading the minus sign as a move to the left.
          trap(px - h, 'shift-direction-inverted'),
          // Applying the outside change to the x-coordinate instead.
          ...(px + k === px - h || px + k === px + h ? [] : [trap(px + k, 'inside-outside-swapped')]),
        ],
      });
    },
  }),

  template({
    id: 'vertex-of-transformed',
    conceptId: 'function-transformations',
    description: 'Read the vertex off a parabola in completed-square form.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const h = rng.int(1, 9) * (rng.bool() ? 1 : -1);
      const k = rng.int(-9, 9) || 5;

      return numericExercise({
        prompt: 'What is the x-coordinate of the vertex?',
        latex: `y = (x ${h < 0 ? '+' : '-'} ${Math.abs(h)})^{2} ${k < 0 ? '-' : '+'} ${Math.abs(k)}`,
        value: h,
        summary: `The vertex of y = x² sits at x = 0, and the inside change moves it to x = ${h}. The ${k < 0 ? '−' : '+'}${Math.abs(k)} moves it vertically, which does not affect this answer.`,
        hint: 'What value of x makes the squared bracket zero?',
        steps: [
          { label: 'Set the bracket to zero.', latex: `x ${h < 0 ? '+' : '-'} ${Math.abs(h)} = 0` },
          { label: 'Solve.', latex: `x = ${h}` },
        ],
        traps: [
          // Reading the sign in the bracket as the direction of the move.
          trap(-h, 'shift-direction-inverted'),
          // Taking the outside number as the horizontal one.
          ...(k === h || k === -h ? [] : [trap(k, 'inside-outside-swapped')]),
        ],
      });
    },
  }),

  /* ------------------------------------------------------------ inverses */
  template({
    id: 'inverse-value',
    conceptId: 'inverse-functions',
    description: 'Evaluate an inverse function at a point.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const m = rng.int(2, difficulty === 3 ? 9 : 5);
      const c = rng.int(-9, 9) || 3;
      const answer = rng.int(1, 12);
      const input = m * answer + c;
      const forwards = m * input + c;

      return numericExercise({
        prompt: `f(x) = ${m}x ${c < 0 ? '-' : '+'} ${Math.abs(c)}. What is f⁻¹(${input})?`,
        latex: `f^{-1}(${input})`,
        value: answer,
        summary: `f⁻¹ undoes f, so the question is which input gives ${input}: ${m} × ${answer} ${c < 0 ? '−' : '+'} ${Math.abs(c)} = ${input}, so f⁻¹(${input}) = ${answer}.`,
        hint: 'Ask what f would have to be given to produce this number.',
        steps: [
          { label: 'Set f(x) equal to the value.', latex: `${m}x ${c < 0 ? '-' : '+'} ${Math.abs(c)} = ${input}` },
          { label: 'Solve for x.', latex: `x = ${answer}` },
        ],
        // Running the function forwards instead of backwards.
        traps: [...(forwards === answer ? [] : [trap(forwards, 'inverse-not-swapped')])],
      });
    },
  }),

  template({
    id: 'inverse-rule',
    conceptId: 'inverse-functions',
    description: 'Choose the rule for an inverse function.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const m = rng.int(2, 9);
      const c = rng.int(1, 9);

      return choiceExercise({
        prompt: `What is the inverse of f(x) = ${m}x + ${c}?`,
        latex: `f(x) = ${m}x + ${c}`,
        ...options(
          [
            { latex: `f^{-1}(x) = \\frac{x - ${c}}{${m}}`, correct: true },
            { latex: `f^{-1}(x) = \\frac{1}{${m}x + ${c}}`, misconceptionId: 'inverse-as-reciprocal' },
            { latex: `f^{-1}(x) = \\frac{x + ${c}}{${m}}`, misconceptionId: 'inverse-not-swapped' },
            { latex: `f^{-1}(x) = ${m}x - ${c}`, misconceptionId: 'inverse-not-swapped' },
          ],
          rng.shuffle,
        ),
        summary: `Write y = ${m}x + ${c}, swap x and y to get x = ${m}y + ${c}, then solve: y = (x − ${c}) / ${m}.`,
        hint: 'Undo the steps in reverse order: f multiplies then adds, so the inverse subtracts then divides.',
      });
    },
  }),

  template({
    id: 'inverse-two-step',
    conceptId: 'inverse-functions',
    description: 'Undo two operations in the right order.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const m = rng.int(2, 9);
      const c = rng.int(1, 9);
      const input = rng.int(2, 12);
      // f(x) = (x + c) / m, so f⁻¹(v) = mv − c.
      const target = m * input - c;

      return numericExercise({
        prompt: `f(x) = (x + ${c}) ÷ ${m}. What is f⁻¹(${input})?`,
        latex: `f(x) = \\frac{x + ${c}}{${m}}`,
        value: target,
        summary: `f adds ${c} and then divides by ${m}, so f⁻¹ multiplies by ${m} and then subtracts ${c}: ${m} × ${input} − ${c} = ${target}.`,
        hint: 'Undo the last operation first — the same reason shoes come off before socks.',
        steps: [
          { label: 'Undo the division.', latex: `${m} \\times ${input} = ${m * input}` },
          { label: 'Undo the addition.', latex: `${m * input} - ${c} = ${target}` },
        ],
        // Applying f rather than its inverse.
        traps: [...((input + c) / m === target ? [] : [trap((input + c) / m, 'inverse-not-swapped')])],
      });
    },
  }),

  /* ---------------------------------------------------------- logarithms */
  template({
    id: 'log-evaluate',
    conceptId: 'logarithms',
    description: 'Evaluate a logarithm by asking what power is needed.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const base = rng.pick([2, 3, 4, 5, 6, 7, 10]);
      const power = rng.int(2, difficulty === 3 ? 5 : 3);
      const value = base ** power;

      return numericExercise({
        prompt: `What is log to the base ${base} of ${value}?`,
        latex: `\\log_{${base}}(${value})`,
        value: power,
        summary: `The question is "${base} to what power gives ${value}?" — and ${base}^${power} = ${value}, so the answer is ${power}.`,
        hint: 'Read it aloud as a question about a power.',
        steps: [
          { label: 'Rewrite it as an exponential.', latex: `${base}^{y} = ${value}` },
          { label: 'Match the powers.', latex: `y = ${power}` },
        ],
        traps: [
          // Answering with the base rather than the exponent.
          ...(base === power ? [] : [trap(base, 'log-base-confused')]),
          // Answering with the argument.
          ...(value === power || value === base ? [] : [trap(value, 'log-base-confused')]),
        ],
      });
    },
  }),

  template({
    id: 'log-solve',
    conceptId: 'logarithms',
    description: 'Solve a logarithmic equation by rewriting it as an exponential.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const base = rng.pick([2, 3, 4, 5, 10]);
      const power = rng.int(2, base <= 3 ? 5 : 3);
      const shift = rng.int(1, 9);
      const answer = base ** power - shift;

      return numericExercise({
        prompt: 'Solve for x.',
        latex: `\\log_{${base}}(x + ${shift}) = ${power}`,
        value: answer,
        summary: `Rewriting as an exponential gives x + ${shift} = ${base}^${power} = ${base ** power}, so x = ${answer}.`,
        hint: 'A logarithm equals a power. Rewrite the equation in exponential form.',
        steps: [
          { label: 'Rewrite in exponential form.', latex: `x + ${shift} = ${base}^{${power}}` },
          { label: 'Evaluate the power.', latex: `x + ${shift} = ${base ** power}` },
          { label: 'Solve.', latex: `x = ${answer}` },
        ],
        // Multiplying the base by the power instead of raising it.
        traps: [...(base * power - shift === answer ? [] : [trap(base * power - shift, 'log-base-confused')])],
      });
    },
  }),

  template({
    id: 'log-law-combine',
    conceptId: 'logarithms',
    description: 'Combine two logarithms into one.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const subtract = rng.bool();
      const a = rng.int(2, 9);
      // 2 + 2 and 2 × 2 are both 4, which would make the "added them instead"
      // distractor identical to the correct answer.
      const b = rng.int(3, 9);

      return choiceExercise({
        prompt: 'Write this as a single logarithm.',
        latex: subtract ? `\\log(${a}x) - \\log(${b})` : `\\log(${a}x) + \\log(${b})`,
        ...options(
          [
            {
              latex: subtract ? `\\log\\!\\left(\\frac{${a}x}{${b}}\\right)` : `\\log(${a * b}x)`,
              correct: true,
            },
            {
              latex: subtract ? `\\frac{\\log(${a}x)}{\\log(${b})}` : `\\log(${a}x + ${b})`,
              misconceptionId: subtract ? 'log-quotient-divided' : 'log-of-sum-split',
            },
            {
              latex: subtract ? `\\log(${a}x - ${b})` : `\\log(${a}x) \\cdot \\log(${b})`,
              misconceptionId: subtract ? 'log-of-sum-split' : 'log-quotient-divided',
            },
            { latex: subtract ? `\\log(${b}x)` : `\\log(${a + b}x)` },
          ],
          rng.shuffle,
        ),
        summary: subtract
          ? `A difference of logarithms is the logarithm of a quotient: log(${a}x) − log(${b}) = log(${a}x / ${b}).`
          : `A sum of logarithms is the logarithm of a product: log(${a}x) + log(${b}) = log(${a * b}x).`,
        hint: 'The log laws turn multiplication into addition and division into subtraction. Read them backwards.',
      });
    },
  }),

  template({
    id: 'log-law-invalid',
    conceptId: 'logarithms',
    description: 'Reject the two log laws that do not exist.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const a = rng.int(2, 12);
      // As above: a + b must differ from a × b, or the law and the error that
      // breaks it become the same option.
      const b = rng.int(3, 12);

      return choiceExercise({
        prompt: 'Which of these is correct?',
        latex: `\\log(${a}) \\;\\text{and}\\; \\log(${b})`,
        ...options(
          [
            { latex: `\\log(${a}) + \\log(${b}) = \\log(${a * b})`, correct: true },
            { latex: `\\log(${a}) + \\log(${b}) = \\log(${a + b})`, misconceptionId: 'log-of-sum-split' },
            { latex: `\\log(${a}) - \\log(${b}) = \\frac{\\log(${a})}{\\log(${b})}`, misconceptionId: 'log-quotient-divided' },
            { latex: `\\log(${a} + ${b}) = \\log(${a}) + \\log(${b})`, misconceptionId: 'log-of-sum-split' },
          ],
          rng.shuffle,
        ),
        // Names the law rather than pointing at a position: the options are
        // shuffled, so "the first one" means nothing by the time it is read.
        summary: `Only log(${a}) + log(${b}) = log(${a * b}) is a law. Logarithms turn products into sums — nothing turns a sum inside the log into anything, and a quotient becomes a subtraction rather than a division of two logs.`,
        hint: 'Test one with numbers you know: log(10) + log(10) = 2, and log(20) is not 2.',
      });
    },
  }),

  /* -------------------------------------------------------- unit circle */
  template({
    id: 'unit-circle-coordinate',
    conceptId: 'unit-circle',
    description: 'Read sine or cosine off the unit circle at a standard angle.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const angle = rng.pick(Object.keys(CIRCLE_VALUES).map(Number));
      const entry = CIRCLE_VALUES[angle] as (typeof CIRCLE_VALUES)[number];
      const wantCosine = rng.bool();
      const correct = wantCosine ? entry.cos : entry.sin;
      const other = wantCosine ? entry.sin : entry.cos;
      // The summary is prose, so it needs the plain-text form of the value.
      const correctText = wantCosine ? entry.cosText : entry.sinText;
      const quadrant = Math.floor(angle / 90) + 1;

      return choiceExercise({
        prompt: `What is ${wantCosine ? 'cos' : 'sin'} ${angle}° exactly?`,
        figure: { kind: 'unit-circle', angle, showCoordinates: false, showQuadrantLabels: true },
        ...options(
          [
            { latex: correct, correct: true },
            { latex: flipSign(correct), misconceptionId: 'quadrant-sign-ignored' },
            { latex: other, misconceptionId: 'sine-cosine-coordinates-swapped' },
            { latex: flipSign(other), misconceptionId: 'sine-cosine-coordinates-swapped' },
          ],
          rng.shuffle,
        ),
        summary: `${angle}° sits in quadrant ${quadrant}, and its reference angle of ${entry.reference}° gives the size. ${wantCosine ? 'Cosine is the across-coordinate' : 'Sine is the up-coordinate'} of the point, and the quadrant supplies the sign, so the value is ${correctText}.`,
        hint: 'The point is (cos θ, sin θ): cosine is across, sine is up. Which quadrant is it in?',
      });
    },
  }),

  template({
    id: 'quadrant-sign',
    conceptId: 'unit-circle',
    description: 'Decide the sign of a trigonometric value from its quadrant.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      /*
       * Never a quadrantal angle. At 90°, 180° and 270° one of the two values
       * is exactly zero, which is neither positive nor negative — the question
       * would have no correct option among the four offered.
       */
      const angle = rng.pick(
        Array.from({ length: 62 }, (_, i) => (i + 10) * 5).filter((value) => value % 90 !== 0),
      );
      const wantCosine = rng.bool();
      const radians = (angle * Math.PI) / 180;
      const value = wantCosine ? Math.cos(radians) : Math.sin(radians);
      const positive = value > 0;
      const quadrant = Math.floor(angle / 90) + 1;

      return choiceExercise({
        prompt: `Is ${wantCosine ? 'cos' : 'sin'} ${angle}° positive or negative?`,
        figure: { kind: 'unit-circle', angle, showQuadrantLabels: true },
        ...options(
          [
            { label: positive ? 'Positive' : 'Negative', correct: true },
            { label: positive ? 'Negative' : 'Positive', misconceptionId: 'quadrant-sign-ignored' },
            { label: 'Zero' },
            { label: 'It depends on which way you measure the angle' },
          ],
          rng.shuffle,
        ),
        summary: `${angle}° lands in quadrant ${quadrant}, where the ${wantCosine ? 'x-coordinate is ' : 'y-coordinate is '}${positive ? 'positive' : 'negative'} — and ${wantCosine ? 'cosine reads the x-coordinate' : 'sine reads the y-coordinate'}.`,
        hint: 'Cosine is the across-coordinate and sine is the up-coordinate. Which side of the axes does the point sit on?',
      });
    },
  }),

  template({
    id: 'radians-to-degrees',
    conceptId: 'unit-circle',
    description: 'Convert an angle in radians to degrees.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const denominator = rng.pick([2, 3, 4, 5, 6, 9, 10, 12, 15, 18, 20, 30, 36]);
      /*
       * Coprime, so the angle is written the way anyone would write it. 6π/9 is
       * a correct name for 2π/3 and nobody has ever used it, and an exercise
       * that prints one is telling the student something false about notation.
       */
      const numerator = rng.pick(
        Array.from({ length: denominator - 1 }, (_, i) => i + 1).filter((n) => gcd(n, denominator) === 1),
      );
      const degrees = (180 * numerator) / denominator;

      return numericExercise({
        prompt: 'Convert this angle to degrees.',
        latex: `\\frac{${numerator === 1 ? '' : numerator}\\pi}{${denominator}}`,
        unit: '°',
        value: degrees,
        summary: `π radians is 180°, so multiply by 180/π: (${numerator}/${denominator}) × 180 = ${degrees}°.`,
        hint: 'Half a turn is 180° and also π radians. Replace the π with 180.',
        steps: [
          { label: 'Substitute 180 for π.', latex: `\\frac{${numerator} \\times 180}{${denominator}}` },
          { label: 'Work it out.', latex: `${degrees}` },
        ],
        // The fraction inverted — 180 × denominator ÷ numerator, which is the
        // conversion run the wrong way round.
        traps: [
          ...((180 * denominator) / numerator === degrees
            ? []
            : [trap((180 * denominator) / numerator, 'degrees-radians-mixed')]),
        ],
      });
    },
  }),

  template({
    id: 'reference-angle',
    conceptId: 'unit-circle',
    description: 'Find the reference angle of an obtuse or reflex angle.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      // Quadrantal angles have a reference angle of 0 or 90, which teaches the
      // idea badly and makes the wrong-axis distractor coincide with the answer.
      const angle = rng.pick(
        Array.from({ length: 53 }, (_, i) => (i + 19) * 5).filter((value) => value % 90 !== 0),
      );
      const quadrant = Math.floor(angle / 90) + 1;
      const reference = quadrant === 2 ? 180 - angle : quadrant === 3 ? angle - 180 : 360 - angle;
      // Measured to the nearest axis rather than to the horizontal one.
      const toVertical = quadrant === 2 ? angle - 90 : quadrant === 3 ? 270 - angle : angle - 270;

      return numericExercise({
        prompt: 'What is the reference angle? Give your answer in degrees.',
        latex: `${angle}^{\\circ}`,
        unit: '°',
        value: reference,
        summary: `${angle}° is in quadrant ${quadrant}, so the acute angle to the nearest part of the x-axis is ${reference}°.`,
        hint: 'The reference angle is the acute angle between the arm and the horizontal axis — never more than 90°.',
        steps: [
          { label: 'Identify the quadrant.', latex: `\\text{quadrant } ${quadrant}` },
          {
            label: 'Measure to the nearest half of the x-axis.',
            latex: quadrant === 2 ? `180 - ${angle} = ${reference}` : quadrant === 3 ? `${angle} - 180 = ${reference}` : `360 - ${angle} = ${reference}`,
          },
        ],
        traps: [...(toVertical === reference ? [] : [trap(toVertical, 'reference-angle-from-wrong-axis')])],
      });
    },
  }),

  /* --------------------------------------------------------- trig graphs */
  template({
    id: 'wave-amplitude',
    conceptId: 'trig-graphs',
    description: 'Read the amplitude of a transformed wave.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const a = rng.int(2, difficulty === 3 ? 12 : 7);
      const b = rng.int(2, 6);
      const cosine = rng.bool();

      return numericExercise({
        prompt: 'What is the amplitude of this wave?',
        latex: `y = ${a}\\${cosine ? 'cos' : 'sin'}(${b}x)`,
        value: a,
        summary: `The number outside the function stretches it vertically, so the wave runs from −${a} to ${a} and its amplitude — measured from the middle line to the peak — is ${a}.`,
        hint: 'Amplitude is measured from the middle line to the peak, not from bottom to top.',
        steps: [
          { label: 'The outside coefficient is the amplitude.', latex: `${a}` },
        ],
        // The full rise and fall, which is twice the amplitude.
        traps: [trap(2 * a, 'amplitude-taken-as-range')],
      });
    },
  }),

  template({
    id: 'wave-period',
    conceptId: 'trig-graphs',
    description: 'Find the period of a wave from the coefficient inside.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = rng.int(2, 9);
      const b = rng.int(2, 8);
      const cosine = rng.bool();

      return fractionExercise({
        prompt: 'The period of this wave is a multiple of π. What is that multiple?',
        latex: `y = ${a}\\${cosine ? 'cos' : 'sin'}(${b}x)`,
        value: fraction(2, b),
        requireSimplified: true,
        // `toText`, not `toLatex` — a summary is rendered as plain prose, so a
        // \frac{}{} would reach the student as literal backslashes.
        summary: `The period is 2π ÷ ${b} = ${toText(fraction(2, b))}π. A larger coefficient inside makes the wave shorter, not longer.`,
        hint: 'The plain wave repeats every 2π. What does the coefficient inside do to that?',
        steps: [
          { label: 'Start from the full period.', latex: '2\\pi' },
          { label: 'Divide by the coefficient inside.', latex: `\\frac{2\\pi}{${b}}` },
        ],
        // Reading the coefficient itself as the period.
        traps: [fractionTrap(fraction(b), 'period-read-as-coefficient')],
      });
    },
  }),

  template({
    id: 'wave-from-extremes',
    conceptId: 'trig-graphs',
    description: 'Find the amplitude of a wave described by its highest and lowest values.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const middle = rng.int(3, 20);
      const amplitude = rng.int(1, middle - 1);
      const high = middle + amplitude;
      const low = middle - amplitude;

      return numericExercise({
        prompt: `A wave rises to ${high} and falls to ${low}. What is its amplitude?`,
        value: amplitude,
        summary: `The middle line is halfway between them, at ${middle}, and the amplitude is the distance from there to the peak: ${high} − ${middle} = ${amplitude}.`,
        hint: 'Amplitude is measured from the middle line, so it is half the total rise and fall.',
        steps: [
          { label: 'Find the middle line.', latex: `\\frac{${high} + ${low}}{2} = ${middle}` },
          { label: 'Measure from there to the peak.', latex: `${high} - ${middle} = ${amplitude}` },
        ],
        // The full distance from trough to crest.
        traps: [trap(high - low, 'amplitude-taken-as-range')],
      });
    },
  }),

  template({
    id: 'wave-phase-shift',
    conceptId: 'trig-graphs',
    description: 'Find the phase shift of a wave written as sin(bx − c).',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const b = rng.int(2, 6);
      // The shift is c/b, and it must not come out equal to c or the trap
      // naming the error would grade as the correct answer.
      const c = rng.int(1, 9);
      const cosine = rng.bool();

      return fractionExercise({
        prompt: 'This wave is shifted right by a multiple of π. What is that multiple?',
        latex: `y = \\${cosine ? 'cos' : 'sin'}(${b}x - ${c}\\pi)`,
        value: fraction(c, b),
        requireSimplified: true,
        summary: `Factor the ${b} out first: ${b}x − ${c}π is ${b}(x − ${toText(fraction(c, b))}π). The shift is ${toText(fraction(c, b))}π, because whatever is done to x inside is done to the shift as well.`,
        hint: 'The shift is only readable once the coefficient of x has been taken outside the bracket.',
        steps: [
          { label: 'Factor the coefficient of x out of the bracket.', latex: `${b}x - ${c}\\pi = ${b}\\left(x - \\frac{${c}\\pi}{${b}}\\right)` },
          { label: 'The shift is what is subtracted from x.', latex: `\\frac{${c}}{${b}}\\pi` },
        ],
        // The constant inside read as the shift, without the division.
        traps: [fractionTrap(fraction(c), 'phase-shift-not-divided')],
      });
    },
  }),

  /* -------------------------------------------- combining and composing */
  template({
    id: 'compose-evaluate',
    conceptId: 'function-operations',
    description: 'Evaluate a composition of two linear functions at a number.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const outerSlope = rng.int(2, difficulty === 3 ? 6 : 4);
      const outerShift = pickDistinct(rng, 9, []);
      const innerSlope = rng.int(2, difficulty === 3 ? 6 : 4);
      const innerShift = pickDistinct(rng, 9, []);
      const input = pickDistinct(rng, 6, []);

      const inner = innerSlope * input + innerShift;
      const outer = outerSlope * input + outerShift;
      const answer = outerSlope * inner + outerShift;
      const reversed = innerSlope * outer + innerShift;

      return numericExercise({
        prompt: `What is f(g(${minus(input)}))?`,
        latex: `f(x) = ${expr(leadingTerm(outerSlope), signedConstant(outerShift))}, \\quad g(x) = ${expr(leadingTerm(innerSlope), signedConstant(innerShift))}`,
        value: answer,
        summary: `g(${minus(input)}) = ${minus(inner)}, and feeding that into f gives f(${minus(inner)}) = ${minus(answer)}.`,
        hint: 'The bracketed one runs first. Work from the inside out.',
        steps: [
          { label: 'Run g first.', latex: `g(${input}) = ${inner}` },
          { label: 'Feed the result into f.', latex: `f(${inner}) = ${answer}` },
        ],
        traps: [
          // f run first, then g — the same two machines in the other order.
          trap(reversed, 'composition-order-reversed'),
          // The little circle read as a multiplication sign.
          trap(outer * inner, 'composition-as-product'),
        ],
      });
    },
  }),

  template({
    id: 'compose-rule',
    conceptId: 'function-operations',
    description: 'Write the composition of two linear functions as a single rule.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const outerSlope = rng.int(2, 5);
      const outerShift = pickDistinct(rng, 8, []);
      const innerSlope = rng.int(2, 5);
      const innerShift = pickDistinct(rng, 8, []);

      const slope = outerSlope * innerSlope;
      const shift = outerSlope * innerShift + outerShift;
      const reversedShift = innerSlope * outerShift + innerShift;

      const rule = (constant: number): string =>
        constant === 0 ? `${slope}*x` : `${slope}*x ${constant < 0 ? '-' : '+'} ${Math.abs(constant)}`;

      return expressionExercise({
        prompt: 'Write f(g(x)) as a single expression in x.',
        latex: `f(x) = ${expr(leadingTerm(outerSlope), signedConstant(outerShift))}, \\quad g(x) = ${expr(leadingTerm(innerSlope), signedConstant(innerShift))}`,
        canonical: rule(shift),
        summary: `Substitute the whole of g into f: ${outerSlope}(${expr(leadingTerm(innerSlope), signedConstant(innerShift))}) ${signedConstant(outerShift)} = ${expr(leadingTerm(slope), signedConstant(shift))}.`,
        hint: 'Wherever f has an x, write the entire rule for g — brackets and all.',
        steps: [
          {
            label: 'Substitute g into f.',
            latex: `${outerSlope}(${expr(leadingTerm(innerSlope), signedConstant(innerShift))}) ${signedConstant(outerShift)}`,
          },
          { label: 'Expand and collect.', latex: expr(leadingTerm(slope), signedConstant(shift)) },
        ],
        // g(f(x)): the same product of slopes, and a different constant.
        traps: [...(reversedShift === shift ? [] : [trap(rule(reversedShift), 'composition-order-reversed')])],
      });
    },
  }),

  template({
    id: 'function-sum-evaluate',
    conceptId: 'function-operations',
    description: 'Evaluate a sum or a product of two functions at a number.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const outerSlope = rng.int(2, 6);
      const outerShift = pickDistinct(rng, 9, []);
      const innerSlope = rng.int(2, 6);
      const innerShift = pickDistinct(rng, 9, []);
      const input = pickDistinct(rng, 5, []);
      const multiply = rng.bool();

      const first = outerSlope * input + outerShift;
      const second = innerSlope * input + innerShift;
      const answer = multiply ? first * second : first + second;

      return numericExercise({
        prompt: `What is (f ${multiply ? '·' : '+'} g)(${minus(input)})?`,
        latex: `f(x) = ${expr(leadingTerm(outerSlope), signedConstant(outerShift))}, \\quad g(x) = ${expr(leadingTerm(innerSlope), signedConstant(innerShift))}`,
        value: answer,
        summary: `Put ${minus(input)} into both: f(${minus(input)}) = ${minus(first)} and g(${minus(input)}) = ${minus(second)}, and ${minus(first)} ${multiply ? '×' : '+'} ${minus(second)} = ${minus(answer)}.`,
        hint: 'The same input goes into both functions. Only then are the two outputs combined.',
        steps: [
          { label: 'Evaluate each function.', latex: `f(${input}) = ${first}, \\quad g(${input}) = ${second}` },
          { label: multiply ? 'Multiply the outputs.' : 'Add the outputs.', latex: `${first} ${multiply ? '\\times' : '+'} ${second} = ${answer}` },
        ],
        traps: [
          // One of the two functions evaluated, and the other forgotten.
          trap(first, 'function-sum-one-part-only'),
          ...(multiply ? [] : [trap(first * second, 'composition-as-product')]),
        ],
      });
    },
  }),

  template({
    id: 'compose-quadratic',
    conceptId: 'function-operations',
    description: 'Compose a quadratic with a linear function, where the order really shows.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const lift = pickDistinct(rng, 9, []);
      const slope = rng.int(2, 5);
      const shift = pickDistinct(rng, 6, []);
      const input = pickDistinct(rng, 4, []);

      const inner = slope * input + shift;
      const answer = inner * inner + lift;
      const reversed = slope * (input * input + lift) + shift;

      return numericExercise({
        prompt: `What is f(g(${minus(input)}))?`,
        latex: `f(x) = x^{2} ${signedConstant(lift)}, \\quad g(x) = ${expr(leadingTerm(slope), signedConstant(shift))}`,
        value: answer,
        summary: `g(${minus(input)}) = ${minus(inner)}, and squaring that and adding ${minus(lift)} gives ${minus(answer)}.`,
        hint: 'Squaring last is not the same as squaring first. Which function is inside the brackets?',
        steps: [
          { label: 'Run g first.', latex: `g(${input}) = ${inner}` },
          { label: 'Square, then shift.', latex: `(${inner})^{2} ${signedConstant(lift)} = ${answer}` },
        ],
        // Squared first and scaled afterwards: g(f(x)) instead of f(g(x)).
        traps: [trap(reversed, 'composition-order-reversed')],
      });
    },
  }),

  template({
    id: 'composition-form',
    conceptId: 'function-operations',
    description: 'Pick the expression that really is the composition.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const outerSlope = rng.int(2, 6);
      const outerShift = pickDistinct(rng, 8, []);
      const innerSlope = rng.int(2, 6);
      const innerShift = pickDistinct(rng, 8, []);
      const f = expr(leadingTerm(outerSlope), signedConstant(outerShift));
      const g = expr(leadingTerm(innerSlope), signedConstant(innerShift));

      const { choices, correctId } = options(
        [
          { latex: `${outerSlope}(${g}) ${signedConstant(outerShift)}`, correct: true },
          { latex: `${innerSlope}(${f}) ${signedConstant(innerShift)}`, misconceptionId: 'composition-order-reversed' },
          { latex: `(${f})(${g})`, misconceptionId: 'composition-as-product' },
          { latex: `(${f}) + (${g})` },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which of these is f(g(x))?',
        latex: `f(x) = ${f}, \\quad g(x) = ${g}`,
        choices,
        correctId,
        summary: `f(g(x)) means every x in f is replaced by the whole of g, giving ${outerSlope}(${g}) ${signedConstant(outerShift)}.`,
        hint: 'Which function has the other one substituted into it?',
      });
    },
  }),

  /* ------------------------------------------------- piecewise functions */
  template({
    id: 'piecewise-evaluate',
    conceptId: 'piecewise-functions',
    description: 'Evaluate a two-part piecewise function away from the join.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const join = rng.int(-3, 3);
      const lowerSlope = pickDistinct(rng, 4, []);
      const lowerShift = pickDistinct(rng, 8, []);
      const upperSlope = pickDistinct(rng, 4, [lowerSlope]);
      const upperShift = pickDistinct(rng, 8, []);
      const above = rng.bool();
      const input = above ? join + rng.int(1, 4) : join - rng.int(1, 4);

      const slope = above ? upperSlope : lowerSlope;
      const shift = above ? upperShift : lowerShift;
      const otherSlope = above ? lowerSlope : upperSlope;
      const otherShift = above ? lowerShift : upperShift;

      return numericExercise({
        prompt: `Find f(${minus(input)}).`,
        latex: piecewiseLatex(lowerSlope, lowerShift, upperSlope, upperShift, join),
        value: slope * input + shift,
        summary: `${minus(input)} is ${above ? 'at or above' : 'below'} ${minus(join)}, so the ${above ? 'second' : 'first'} rule applies: ${minus(slope * input + shift)}.`,
        hint: 'Check the condition before you substitute. The input decides which rule you are allowed to use.',
        steps: [
          {
            label: 'Which condition does the input meet?',
            latex: `${input} ${above ? '\\ge' : '<'} ${join}`,
          },
          { label: 'Substitute into that rule only.', latex: `f(${input}) = ${slope * input + shift}` },
        ],
        // The other rule used, though the input never satisfied its condition.
        traps: [trap(otherSlope * input + otherShift, 'piecewise-wrong-branch')],
      });
    },
  }),

  template({
    id: 'piecewise-at-boundary',
    conceptId: 'piecewise-functions',
    description: 'Evaluate a piecewise function exactly where the definition splits.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const join = pickDistinct(rng, 4, []);
      const lowerSlope = pickDistinct(rng, 4, []);
      const lowerShift = pickDistinct(rng, 8, []);
      const upperSlope = pickDistinct(rng, 4, [lowerSlope]);
      const upperShift = pickDistinct(rng, 8, []);

      const answer = upperSlope * join + upperShift;
      const other = lowerSlope * join + lowerShift;

      return numericExercise({
        prompt: `Find f(${minus(join)}) — the value exactly where the rules change.`,
        latex: piecewiseLatex(lowerSlope, lowerShift, upperSlope, upperShift, join),
        value: answer,
        summary: `The first condition is x < ${minus(join)}, which ${minus(join)} does not satisfy; the second is x ≥ ${minus(join)}, which it does. So the second rule owns this point, and f(${minus(join)}) = ${minus(answer)}.`,
        hint: 'One of the two signs has a line underneath it. That is the branch the endpoint belongs to.',
        steps: [
          { label: 'Only one condition includes the endpoint.', latex: `${join} \\ge ${join}` },
          { label: 'Use that rule.', latex: `f(${join}) = ${answer}` },
        ],
        traps: [
          // The strict branch given the endpoint it explicitly excludes.
          trap(other, 'piecewise-endpoint-misassigned'),
          // The condition reported instead of the output.
          ...(join === answer ? [] : [trap(join, 'piecewise-boundary-given-as-value')]),
        ],
      });
    },
  }),

  template({
    id: 'piecewise-branch-choice',
    conceptId: 'piecewise-functions',
    description: 'Decide which rule owns the point where a piecewise function splits.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const join = pickDistinct(rng, 5, []);
      const lowerSlope = pickDistinct(rng, 4, []);
      const lowerShift = pickDistinct(rng, 8, []);
      const upperSlope = pickDistinct(rng, 4, [lowerSlope]);
      const upperShift = pickDistinct(rng, 8, []);
      const lower = expr(leadingTerm(lowerSlope), signedConstant(lowerShift));
      const upper = expr(leadingTerm(upperSlope), signedConstant(upperShift));

      const { choices, correctId } = options(
        [
          { latex: upper, correct: true },
          { latex: lower, misconceptionId: 'piecewise-endpoint-misassigned' },
          { label: 'Both rules, averaged', misconceptionId: 'piecewise-wrong-branch' },
          { label: `f(${minus(join)}) does not exist`, misconceptionId: 'piecewise-boundary-given-as-value' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `Which rule gives f(${minus(join)})?`,
        latex: piecewiseLatex(lowerSlope, lowerShift, upperSlope, upperShift, join),
        choices,
        correctId,
        summary: `x < ${minus(join)} excludes ${minus(join)} and x ≥ ${minus(join)} includes it, so exactly one rule applies there — the second. A piecewise function is still a function: one input, one output.`,
        hint: 'Read the two conditions carefully. Which of them is true when x is exactly the joining value?',
      });
    },
  }),

  template({
    id: 'step-function-band',
    conceptId: 'piecewise-functions',
    description: 'Read a cost from a step function, including at a band boundary.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const firstBreak = rng.int(2, 5);
      const secondBreak = firstBreak + rng.int(2, 5);
      const cheap = rng.int(3, 6);
      const middle = cheap + rng.int(2, 5);
      const dear = middle + rng.int(2, 5);
      const onBoundary = rng.bool();
      const weight = onBoundary ? rng.pick([firstBreak, secondBreak]) : rng.pick([1, firstBreak + 1, secondBreak + 2]);

      const cost = weight <= firstBreak ? cheap : weight <= secondBreak ? middle : dear;
      const next = weight <= firstBreak ? middle : weight <= secondBreak ? dear : middle;

      return numericExercise({
        prompt: `A courier charges by weight: £${cheap} up to and including ${firstBreak} kg, £${middle} above ${firstBreak} kg and up to ${secondBreak} kg, and £${dear} above ${secondBreak} kg. What does a parcel of ${weight} kg cost?`,
        unit: '£',
        value: cost,
        summary: onBoundary
          ? `${weight} kg is exactly on a boundary, and that band is written "up to and including", so the parcel stays in the cheaper band: £${cost}.`
          : `${weight} kg falls in the band charging £${cost}.`,
        hint: 'Bands like these overlap at their edges unless you read the words carefully. Which band claims the boundary?',
        steps: [{ label: 'Find the band that contains the weight.', latex: `f(${weight}) = ${cost}` }],
        traps: [
          onBoundary
            ? // The boundary handed to the band that starts *above* it.
              trap(next, 'piecewise-endpoint-misassigned')
            : trap(next, 'piecewise-wrong-branch'),
        ],
      });
    },
  }),

  /* ------------------------------ exponential and logarithmic equations */
  template({
    id: 'exponential-same-base',
    conceptId: 'exponential-equations',
    description: 'Solve an exponential equation by matching the bases.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const base = rng.pick([2, 3, 5]);
      const slope = rng.int(2, 4);
      const answer = rng.int(1, base === 2 ? 4 : 2);
      const shift = pickDistinct(rng, 5, []);
      const power = slope * answer + shift;
      const right = base ** power;
      const divided = (right / base - shift) / slope;

      return numericExercise({
        prompt: 'Solve for x.',
        latex: `${base}^{${expr(leadingTerm(slope), signedConstant(shift))}} = ${right}`,
        value: answer,
        summary: `${right} is ${base} to the power ${power}, so the exponents must match: ${expr(leadingTerm(slope), signedConstant(shift))} = ${power}, and x = ${answer}.`,
        hint: `Write the right-hand side as a power of ${base}. Once both sides are powers of the same base, the exponents are equal.`,
        steps: [
          { label: 'Write both sides to the same base.', latex: `${base}^{${expr(leadingTerm(slope), signedConstant(shift))}} = ${base}^{${power}}` },
          { label: 'Equate the exponents.', latex: `${expr(leadingTerm(slope), signedConstant(shift))} = ${power}` },
          { label: 'Solve.', latex: `x = ${answer}` },
        ],
        // Dividing by the base, as though the exponent were a coefficient.
        traps: [...(Number.isInteger(divided) ? [trap(divided, 'exponent-divided-out')] : [])],
      });
    },
  }),

  template({
    id: 'exponential-log-solve',
    conceptId: 'exponential-equations',
    description: 'Solve an exponential equation that needs a logarithm.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const base = rng.pick([2, 3, 5, 7]);
      const multiple = rng.int(3, 19);
      const right = base * multiple;
      const answer = round2(Math.log(right) / Math.log(base));

      return numericExercise({
        prompt: 'Solve for x, to two decimal places.',
        latex: `${base}^{x} = ${right}`,
        value: answer,
        tolerance: decimalTolerance(answer, 2),
        summary: `Taking logs of both sides gives x·log ${base} = log ${right}, so x = log ${right} ÷ log ${base} = ${answer}.`,
        hint: 'x is stuck in the exponent, and only a logarithm brings it down.',
        steps: [
          { label: 'Take the log of both sides.', latex: `x \\log ${base} = \\log ${right}` },
          { label: 'Divide.', latex: `x = \\frac{\\log ${right}}{\\log ${base}} \\approx ${answer}` },
        ],
        // Dividing by the base as though b^x meant b times x.
        traps: [trap(multiple, 'exponent-divided-out')],
      });
    },
  }),

  template({
    id: 'log-change-of-base',
    conceptId: 'exponential-equations',
    description: 'Evaluate a logarithm in a base a calculator does not have.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const base = rng.pick([3, 5, 6, 7]);
      const argument = rng.pick([20, 30, 40, 50, 60, 80, 90, 100, 120, 150, 200]);
      const answer = round2(Math.log(argument) / Math.log(base));
      const inverted = round2(Math.log(base) / Math.log(argument));

      return numericExercise({
        prompt: 'Evaluate, to two decimal places.',
        latex: `\\log_{${base}} ${argument}`,
        value: answer,
        tolerance: decimalTolerance(answer, 2),
        summary: `Change of base puts the argument on top: log ${argument} ÷ log ${base} = ${answer}.`,
        hint: `Sanity-check whatever you write down: ${base} to that power should come out near ${argument}.`,
        steps: [
          { label: 'Change the base.', latex: `\\log_{${base}} ${argument} = \\frac{\\log ${argument}}{\\log ${base}}` },
          { label: 'Divide.', latex: `\\approx ${answer}` },
        ],
        // The fraction written upside down.
        traps: [trap(inverted, 'change-of-base-inverted')],
      });
    },
  }),

  template({
    id: 'log-equation-solve',
    conceptId: 'exponential-equations',
    description: 'Solve a logarithmic equation by rewriting it as a power.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const base = rng.pick([2, 3, 5]);
      const power = rng.int(2, base === 2 ? 6 : 4);
      const shift = pickDistinct(rng, 9, []);
      const answer = base ** power - shift;

      return numericExercise({
        prompt: 'Solve for x.',
        latex: `\\log_{${base}}(x ${signedConstant(shift)}) = ${power}`,
        value: answer,
        summary: `A logarithm is a question about a power: this says ${base}^${power} = x ${signedConstant(shift)}, so x ${signedConstant(shift)} = ${base ** power} and x = ${answer}.`,
        hint: `Read it aloud: "${base} to what power gives the bracket?" You are told the power — so raise ${base} to it.`,
        steps: [
          { label: 'Rewrite as a power.', latex: `x ${signedConstant(shift)} = ${base}^{${power}}` },
          { label: 'Evaluate and solve.', latex: `x = ${base ** power} ${signedConstant(-shift)}` },
        ],
        // Every logarithm read as base 10, whatever the little number says.
        traps: [trap(10 ** power - shift, 'log-base-confused')],
      });
    },
  }),

  template({
    id: 'log-equation-extraneous',
    conceptId: 'exponential-equations',
    description: 'Solve a log equation where one candidate leaves the domain.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const negative = -rng.int(1, 6);
      const positive = rng.int(1 - negative, 9);
      const gap = positive + negative;
      const product = -positive * negative;

      return numericExercise({
        prompt: 'Solve for x. One of the two candidates is not a solution.',
        latex: `\\log(x) + \\log(x - ${gap}) = \\log(${product})`,
        value: positive,
        summary: `Adding logs multiplies the arguments, so x(x − ${gap}) = ${product}, whose roots are ${positive} and ${negative}. But log(${negative}) does not exist, so that candidate goes — leaving x = ${positive}.`,
        hint: 'Solve the quadratic, then put both answers back. A logarithm of a negative number is not a number.',
        steps: [
          { label: 'Combine the two logs.', latex: `\\log(x(x - ${gap})) = \\log(${product})` },
          { label: 'Solve the quadratic.', latex: `x = ${positive} \\text{ or } x = ${negative}` },
          { label: 'Check both in the original.', latex: `x = ${positive} \\;\\checkmark, \\quad x = ${negative} \\;\\times` },
        ],
        // The negative root kept, though no logarithm can accept it.
        traps: [trap(negative, 'log-domain-ignored')],
      });
    },
  }),

  template({
    id: 'exponential-growth-time',
    conceptId: 'exponential-equations',
    description: 'Find how long a growing quantity takes to reach a target.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const start = rng.int(2, 9) * 100;
      const rate = rng.pick([3, 4, 5, 6, 8, 10]);
      const factor = rng.pick([2, 3, 4]);
      const target = start * factor;
      const growth = 1 + rate / 100;
      const answer = round1(Math.log(factor) / Math.log(growth));
      const linear = round1((target - start) / (start * (rate / 100)));

      return numericExercise({
        prompt: `A colony of ${start} bacteria grows by ${rate}% each hour. After how many hours does it reach ${target}? Give your answer to one decimal place.`,
        unit: 'hours',
        value: answer,
        tolerance: decimalTolerance(answer, 1),
        summary: `${start} × ${growth}^t = ${target} gives ${growth}^t = ${factor}, so t = log ${factor} ÷ log ${growth} ≈ ${answer} hours.`,
        hint: 'Divide by the starting amount first. What is left is a single power to undo.',
        steps: [
          { label: 'Divide by the start.', latex: `${growth}^{t} = ${factor}` },
          { label: 'Take logs and divide.', latex: `t = \\frac{\\log ${factor}}{\\log ${growth}} \\approx ${answer}` },
        ],
        // Growth of "rate% of the original each hour" — which is not what it does.
        traps: [...(Math.abs(linear - answer) < 0.2 ? [] : [trap(linear, 'exponential-treated-as-linear')])],
      });
    },
  }),

  template({
    id: 'exponential-method',
    conceptId: 'exponential-equations',
    description: 'Choose the move that frees an unknown from an exponent.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const base = rng.pick([2, 3, 5, 7]);
      const right = base * rng.int(3, 12) + rng.int(1, base - 1);

      const { choices, correctId } = options(
        [
          { label: 'Take the logarithm of both sides', correct: true },
          { label: `Divide both sides by ${base}`, misconceptionId: 'exponent-divided-out' },
          { label: `Write log(${base}^x) = ${right}`, misconceptionId: 'log-taken-on-one-side' },
          { label: `Take the ${base}th root of both sides` },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which move gets x out of the exponent?',
        latex: `${base}^{x} = ${right}`,
        choices,
        correctId,
        summary: `A logarithm is the operation that undoes a power, and like every other operation it has to be applied to both sides. That gives x·log ${base} = log ${right}.`,
        hint: 'Roots undo a power when the unknown is in the base. Here the unknown is the exponent itself.',
      });
    },
  }),
];
